import React, { useState, useCallback } from "react";
import {
  Pane,
  TextInputField,
  TextareaField,
  Button,
  toaster,
  FileUploader,
  FileCard,
} from "evergreen-ui";
import * as pdfjsLib from "pdfjs-dist";
import mammoth from "mammoth";
import { Document, Packer, Paragraph, TextRun } from "docx";
import "pdfjs-dist/build/pdf.worker.mjs";
import "./AdminBlog.css";

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.mjs",
  import.meta.url
).toString();

function AdminBlog() {
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [content, setContent] = useState("");
  const [image, setImage] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const [imageRejections, setImageRejections] = useState([]);
  const [docRejections, setDocRejections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BASE_URL;

  // ----------------------------------------------------------------------
  // 1. Download Blog Template
  // ----------------------------------------------------------------------
  const handleDownloadTemplate = async () => {
    const doc = new Document({
      sections: [
        {
          children: [
            new Paragraph({
              children: [new TextRun({ text: "Blog Template", bold: true, size: 28 })],
            }),
            new Paragraph(" "),
            new Paragraph({
              children: [
                new TextRun({ text: "Title: ", bold: true }),
                new TextRun("____________________"),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: "Author: ", bold: true }),
                new TextRun("___________________"),
              ],
            }),
            new Paragraph(" "),
            new Paragraph({ children: [new TextRun({ text: "Content:", bold: true })] }),
            new Paragraph("Write your full blog content here..."),
          ],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Blog_Template.docx";
    link.click();
  };

  // ----------------------------------------------------------------------
  // 2. Handle document upload (PDF or Word)
  // ----------------------------------------------------------------------
  const handleDocUpload = async (files) => {
    const file = files?.[0];
    if (!file) return;

    setDocFile(file);
    setDocRejections([]);
    setExtracting(true);

    try {
      let rawText = "";
      if (file.type === "application/pdf") {
        rawText = await extractTextFromPDF(file);
      } else if (
        file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ) {
        rawText = await extractTextFromWord(file);
      } else {
        toaster.danger("Please upload a Word (.docx) or PDF file.");
        setDocFile(null);
        return;
      }

      fillFieldsFromExtractedText(rawText);
      toaster.success("Fields auto-filled from the uploaded document!");
    } catch (err) {
      console.error("Extraction error:", err);
      toaster.danger("Failed to read the document.");
    } finally {
      setExtracting(false);
    }
  };

  // ----------------------------------------------------------------------
  // 3. Improved PDF extraction (line-based and label aware)
  // ----------------------------------------------------------------------
  // ----------------------------------------------------------------------
// 🧩 IMPROVED PDF TEXT EXTRACTION (robust, normalized, auto line breaks)
// ----------------------------------------------------------------------
const extractTextFromPDF = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  let allText = "";

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const textContent = await page.getTextContent();

    const pageText = textContent.items
      .map((item) => item.str.trim())
      .filter(Boolean)
      .join(" ");

    allText += pageText + "\n\n";
  }

  // Normalize broken words and spacing
  let normalized = allText
    .replace(/\r/g, "\n")
    .replace(/\s{2,}/g, " ")
    .replace(/Aut\s*hor/gi, "Author") // fix split word
    .replace(/Tit\s*le/gi, "Title")
    .replace(/Cont\s*ent/gi, "Content")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  // Insert clear breaks before known labels
  normalized = normalized
    .replace(/\s*(Title\s*[:-])/gi, "\n$1 ")
    .replace(/\s*(Author\s*[:-])/gi, "\n$1 ")
    .replace(/\s*(Content\s*[:-])/gi, "\n$1 ");

  return normalized.trim();
};

// ----------------------------------------------------------------------
// 🧩 SMART FIELD MAPPER (handles merged or missing labels gracefully)
// ----------------------------------------------------------------------
const fillFieldsFromExtractedText = (rawText) => {
  if (!rawText) {
    toaster.warning("No text found in the document.");
    return;
  }

  // Clean up weird formatting
  let text = rawText
    .replace(/\r/g, "\n")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{2,}/g, "\n")
    .trim();

  // Handle case where title, author, and content are on one or two lines
  const labelRegex =
    /(Title\s*[:-]\s*)(.*?)\s*(Author\s*[:-]\s*)(.*?)\s*(Content\s*[:-]\s*)([\s\S]*)/i;
  const match = text.match(labelRegex);

  let title = "";
  let author = "";
  let content = "";

  if (match) {
    title = match[2]?.trim() || "";
    author = match[4]?.trim() || "";
    content = match[6]?.trim() || "";
  } else {
    // Fallback – handle partial structure
    const titleMatch = text.match(/Title\s*[:-]\s*(.*)/i);
    const authorMatch = text.match(/Author\s*[:-]\s*(.*)/i);
    const contentMatch = text.match(/Content\s*[:-]\s*([\s\S]*)/i);

    if (titleMatch) title = titleMatch[1].trim();
    if (authorMatch) author = authorMatch[1].trim();
    if (contentMatch) content = contentMatch[1].trim();

    // Fix if author accidentally contains title
    if (author?.toLowerCase().includes("title:"))
      author = author.split(/title\s*[:-]/i).pop().trim();
  }

  // Extra cleanup
  title = title.replace(/^by\s+/i, "").replace(/^t\s*/i, "").trim();
  author = author.replace(/^by\s+/i, "").replace(/^t\s*/i, "").trim();

  // Fallbacks if still missing
  if (!title && text.split("\n")[0])
    title = text.split("\n")[0].replace(/^Title\s*[:-]\s*/i, "").trim();
  if (!author && text.split("\n")[1])
    author = text
      .split("\n")[1]
      .replace(/^Author\s*[:-]\s*/i, "")
      .replace(/^by\s+/i, "")
      .trim();
  if (!content) {
    const idx = text.toLowerCase().indexOf("content");
    content = idx !== -1 ? text.slice(idx + 8).trim() : text;
  }

  // Set fields
  if (title) setTitle(title);
  if (author) setAuthor(author);
  if (content) setContent(content);

  const missing = [];
  if (!title) missing.push("title");
  if (!author) missing.push("author");
  if (!content) missing.push("content");

  if (missing.length) {
    toaster.warning(
      `Could not extract: ${missing.join(", ")}. Please include "Title:", "Author:", and "Content:" labels in your file.`
    );
  }
};


  // ----------------------------------------------------------------------
  // 4. Word extraction using Mammoth
  // ----------------------------------------------------------------------
  const extractTextFromWord = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value.trim();
  };

  

  // ----------------------------------------------------------------------
  // 6. Image handling
  // ----------------------------------------------------------------------
  const handleImageChange = useCallback((files) => {
    if (files.length) {
      setImage(files[0]);
      setImageRejections([]);
    }
  }, []);

  const handleImageRejected = useCallback((rejections) => {
    setImageRejections(rejections);
    toaster.danger("Invalid image. Max 10 MB, one file only.");
  }, []);

  const handleImageRemove = useCallback(() => {
    setImage(null);
    setImageRejections([]);
  }, []);

  // ----------------------------------------------------------------------
  // 7. Submit new blog
  // ----------------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title || !author || !content || !image) {
      toaster.danger("All fields + an image are required.");
      return;
    }

    const form = new FormData();
    form.append("title", title);
    form.append("author", author);
    form.append("content", content);
    form.append("image", image);

    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/blog`, {
        method: "POST",
        body: form,
      });

      if (!res.ok) throw new Error("Network error");

      toaster.success("Blog created successfully!");
      setTitle("");
      setAuthor("");
      setContent("");
      setImage(null);
      setDocFile(null);
      setImageRejections([]);
      setDocRejections([]);
    } catch (err) {
      console.error(err);
      toaster.danger("Failed to create the blog.");
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------------------
  // 8. Render UI
  // ----------------------------------------------------------------------
  return (
    <Pane className="admin-blog-container">
      <h2 className="blog-title">Create New Blog</h2>

      <form className="admin-blog-form" onSubmit={handleSubmit}>
        <div className="columns-container">
          {/* LEFT COLUMN */}
          <div className="left-column">
            <TextInputField
              label="Blog Title"
              placeholder="Enter blog title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />

            <TextInputField
              label="Author"
              placeholder="Enter author name"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              required
            />

            <TextareaField
              label="Content"
              placeholder="Write or auto-fill blog content..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              required
              minHeight={180}
            />

            <div className="image-picker">
              <label>Upload Blog Image</label>
              <FileUploader
                label="Upload Image"
                description="Max 10 MB, one image"
                maxSizeInBytes={10 * 1024 ** 2}
                maxFiles={1}
                onChange={handleImageChange}
                onRejected={handleImageRejected}
                renderFile={(file) => (
                  <FileCard
                    key={file.name}
                    name={file.name}
                    sizeInBytes={file.size}
                    type={file.type}
                    onRemove={handleImageRemove}
                  />
                )}
                values={image ? [image] : []}
              />

              {imageRejections.length > 0 && (
                <ul className="file-rejections">
                  {imageRejections.map(({ file, errors }) => (
                    <li key={file.name} className="rejection-item">
                      ❌ <strong>{file.name}</strong> —{" "}
                      {errors.map((e) => e.message).join(", ")}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* DIVIDER */}
          <div className="divider" />

          {/* RIGHT COLUMN */}
          <div className="right-column">
            <div className="file-section">
              <h4>Auto-fill from Template</h4>
              <p className="template-desc">
                Download the Word template, fill it, then upload it here.
              </p>

              <Button
                appearance="minimal"
                intent="success"
                onClick={handleDownloadTemplate}
              >
                ⬇️ Download Template
              </Button>

              <hr className="template-divider" />

              <FileUploader
                label="Upload Filled Template"
                description="Word (.docx) or PDF, max 50 MB"
                maxSizeInBytes={50 * 1024 ** 2}
                maxFiles={1}
                onChange={handleDocUpload}
                onRejected={setDocRejections}
                renderFile={(file) => (
                  <FileCard
                    key={file.name}
                    name={file.name}
                    sizeInBytes={file.size}
                    type={file.type}
                    onRemove={() => setDocFile(null)}
                  />
                )}
                values={docFile ? [docFile] : []}
              />

              {docRejections.length > 0 && (
                <ul className="file-rejections">
                  {docRejections.map(({ file, errors }) => (
                    <li key={file.name} className="rejection-item">
                      ❌ <strong>{file.name}</strong> —{" "}
                      {errors.map((e) => e.message).join(", ")}
                    </li>
                  ))}
                </ul>
              )}

              {extracting && <p className="loading-text">Extracting text…</p>}
            </div>
          </div>
        </div>

        <Button
          appearance="primary"
          intent="success"
          height={44}
          iconBefore="add"
          type="submit"
          isLoading={loading}
        >
          {loading ? "Creating…" : "Create Blog"}
        </Button>
      </form>
    </Pane>
  );
}

export default AdminBlog;
